/** @format */
import { TOptions } from 'i18next';
import { i18n as i18next } from 'next-i18next';

import { normalizeIsoCode } from '@toucan/common/helpers/language';
import { getConfig } from '@toucan/common/services/nextjs/settings';
import { TaxonomyTermType } from '@toucan/common/services/taxonomy/config/constants';
import {
    Taxonomy,
    TaxonomyTermPage,
} from '@toucan/common/services/taxonomy/config/types';

import { AvailableMarket, MarketCode } from '@toucan/client/global/market';
import { ModelTranslations } from '@toucan/client/global/model';

import { removeTrailingSlash } from './helpers';

export enum UrlFactoryType {
    DEFAULT,
    HOMEPAGE,
    TAXONOMY,
    ARTICLE,
    BASIC_PAGE,
    ARTICLE_CATEGORIES,
    MODEL,
}

type UrlSlugReference = Record<string, string>;

type UrlProvider = (market: AvailableMarket) => string | undefined | null;

type UrlMapping = {
    [key in MarketCode]?: string;
};

export class UrlFactory {
    private readonly urlPrefix: string;
    private readonly urlProvider: UrlProvider;

    public constructor(urlProvider: UrlProvider) {
        const { publicRuntimeConfig } = getConfig();

        this.urlPrefix = publicRuntimeConfig.hostUri;
        this.urlProvider = urlProvider;
    }

    public getUrls(availableMarkets: AvailableMarket[]): UrlMapping {
        return availableMarkets.reduce(
            (accumulator: UrlMapping, market: AvailableMarket): UrlMapping => {
                const urlSuffix = this.urlProvider(market);

                if (typeof urlSuffix === 'string') {
                    accumulator[market.code] = removeTrailingSlash(
                        `${this.urlPrefix}/${market.slug}/${urlSuffix}`
                    );
                }
                return accumulator;
            },
            {}
        );
    }
}

export const urlFactories = {
    /**
     * The sell form URL Factory, composing known static url parts into
     * a canonical reference
     */
    [UrlFactoryType.DEFAULT]: (urlSlug: string | string[]) =>
        new UrlFactory((market) => {
            const urlSlugArray = ([] as string[]).concat(urlSlug);
            return urlSlugArray
                .map((slug) =>
                    i18next?.t(slug, {
                        ns: 'urls',
                        lng: normalizeIsoCode(market.iso),
                    })
                )
                .join('/');
        }),

    /**
     * A homepage URL Factory, mostly just here for brevity.
     */
    [UrlFactoryType.HOMEPAGE]: new UrlFactory((market) => {
        return '';
    }),

    /**
     * Taxonomy page URL Factory that transforms a provided TaxonomyTermPage
     * into a canonical reference using provided data
     *
     * @param {UrlSlugReference} urlSlugReferences
     */
    [UrlFactoryType.TAXONOMY]: (
        taxonomyTerm?: Taxonomy,
        predicate?: (page: TaxonomyTermPage, market: AvailableMarket) => boolean
    ) =>
        new UrlFactory((market) => {
            if (!taxonomyTerm) {
                return null;
            }
            const categoryPage = taxonomyTerm.categoryPage.find(
                (page: TaxonomyTermPage) => predicate?.(page, market)
            );

            if (taxonomyTerm.type === TaxonomyTermType.BRAND) {
                return categoryPage?.slug;
            }

            const category = i18next?.t('category', {
                ns: 'urls',
                lng: normalizeIsoCode(market.iso),
            });

            const url = [category, categoryPage?.slug].join('/');
            return url;
        }),

    /**
     * Model page URL Factory that transforms an array of ModelUrlSegmenta
     * into the given market slug
     *
     * @param {urlSegments} ModelUrlSegment
     */
    [UrlFactoryType.MODEL]: (translations?: ModelTranslations[]) =>
        new UrlFactory((market) => {
            if (!translations) {
                return null;
            }
            const slug = i18next?.t('product', {
                ns: 'urls',
                lng: normalizeIsoCode(market.iso),
            });
            const { urlSegment = null } =
                translations.find(({ language }) => language === market.slug) ??
                {};

            if (!urlSegment || !slug) {
                return null;
            }

            return `${slug}/${urlSegment}`;
        }),

    /**
     * Contentful article URL Factory that transforms a static prefix and a
     * dynamic slugs provided by Contentful into a canonical reference
     *
     * @param {UrlSlugReference} urlSlugReferences
     */
    [UrlFactoryType.ARTICLE]: (
        articleSlugReferences?: UrlSlugReference,
        categorySlugReferences?: UrlSlugReference
    ) =>
        new UrlFactory((market) => {
            if (
                !articleSlugReferences?.[market.slug] ||
                !categorySlugReferences?.[market.slug]
            ) {
                return null;
            }

            const contentSlug = i18next?.t('content', {
                ns: 'urls',
                lng: normalizeIsoCode(market.iso),
            });

            const categorySlug = categorySlugReferences[market.slug];
            const articleSlug = articleSlugReferences[market.slug];

            const urlParts = [contentSlug, categorySlug, articleSlug];

            return urlParts.join('/');
        }),

    [UrlFactoryType.ARTICLE_CATEGORIES]: (
        urlSlugReferences?: UrlSlugReference
    ) =>
        new UrlFactory((market) => {
            const marketSlug = market.slug;

            if (!urlSlugReferences?.[marketSlug]) {
                return null;
            }

            const tOptions: TOptions = {
                ns: 'urls',
                lng: marketSlug,
            };

            const urlParts = [
                i18next?.t('content', tOptions),
                urlSlugReferences[marketSlug],
            ];

            return urlParts.join('/');
        }),

    /**
     * Contentful basic pages URL Factory that transforms a static prefix and a
     * dynamic slug provided by Contentful into a canonical reference
     *
     * @param {UrlSlugReference} urlSlugReferences
     */
    [UrlFactoryType.BASIC_PAGE]: (urlSlugReferences?: UrlSlugReference) =>
        new UrlFactory((market) => {
            if (!urlSlugReferences?.[market.slug]) {
                return null;
            }

            return urlSlugReferences[market.slug];
        }),
};
